//src/pages/admin.js
import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { initAdminPush, disableAdminPush } from "../push";
import { fmtDate, fmtNum, fmtUsd } from "../utils/fmt";
import { useToast } from "../utils/useToast";

export default function Admin({ authed, role }) {
  const [msg, setMsg] = useState("");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [settings, setSettings] = useState(null);

  const [userFilter, setUserFilter] = useState("all"); // all | pending | approved | admins | inactive | contact
  const [q, setQ] = useState("");
  const [openUserId, setOpenUserId] = useState("");
  const { toast, showToast } = useToast();
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [pwModal, setPwModal] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [myDefaultRoute, setMyDefaultRoute] = useState("/");

useEffect(() => {
  if (!authed || role !== "admin") return;
  refresh();
  api.adminGetSettings().then((d) => setSettings(d.settings)).catch(() => {});

  // NEW: load my default calculator
  if (myId) {
    api.adminUser(myId)
      .then((d) => setMyDefaultRoute(d?.user?.defaultCalculatorRoute || "/"))
      .catch(() => setMyDefaultRoute("/"));
  }
}, [authed, role]);


  useEffect(() => {
  if (!authed || role !== "admin") return;

  (async () => {
    try {
      if (!("serviceWorker" in navigator)) return setPushEnabled(false);
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) return setPushEnabled(false);
      const sub = await reg.pushManager.getSubscription();
      setPushEnabled(!!sub);
    } catch {
      setPushEnabled(false);
    }
  })();
}, [authed, role]);

  async function refresh() {
    setMsg("");
    try {
      const d = await api.adminUsers();
      setUsers(d.users || []);
    } catch (e) {
      setMsg(e.message);
    }
  }

  async function openUser(id) {
    if (!id) return;
    setMsg("");
    try {
      const d = await api.adminUser(id);
      setSelected(d.user);
      setSelectedLogs(d.logs || []);
      setOpenUserId(id);
      await refresh();
    } catch (e) {
      setMsg(e.message);
    }
  }

  function closeUser() {
      setSelected(null);
      setSelectedLogs([]);
      setOpenUserId("");
    }

    function decodeUserIdFromToken(token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload?.sub || payload?.id || "";
      } catch {
        return "";
      }
    }
    const myId = useMemo(() => decodeUserIdFromToken(localStorage.getItem("token") || ""), []);

  async function patchUser(id, patch) {
    setMsg("");
    try {
      await api.adminPatchUser(id, patch);
      showToast("User updated", "ok");
      await refresh();
      if (selected?.id === id) await openUser(id);
    } catch (e) {
      setMsg(e.message);
    }
  }

  function openPasswordModal(id, username) {
    setPwModal({ id, username, password: "" });
  }

    async function submitPasswordModal() {
    if (!pwModal) return;
    const password = String(pwModal.password || "").trim();
    if (!password) {
      setMsg("Password cannot be empty");
      return;
    }

    setMsg("");
    try {
      await api.adminSetPassword(pwModal.id, password);
      showToast("Password updated", "ok");
      setPwModal(null);
    } catch (e) {
      setMsg(e.message);
    }
  }

  function requestDeleteUser(id, username) {
   setConfirmDelete({ id, username });
  }

  async function confirmDeleteUser() {
  if (!confirmDelete) return;

  const { id } = confirmDelete;
  setMsg("");

  try {
    await api.adminDeleteUser(id);

    if (selected?.id === id) {
      setSelected(null);
      setSelectedLogs([]);
    }

    await refresh();
    showToast("User deleted", "ok");
  } catch (e) {
    setMsg(e.message);
  } finally {
    setConfirmDelete(null);
  }
}

  async function saveSettings() {
    setMsg("");
    try {
      const d = await api.adminPutSettings(settings);
      setSettings(d.settings);
      showToast("Settings saved", "ok");
      setMsg("Settings saved");
    } catch (e) {
      setMsg(e.message);
    }
  }

  const filteredUsers = useMemo(() => {
    const term = q.trim().toLowerCase();

    return (users || [])
      .filter((u) => {
        if (userFilter === "pending") return !u.approved && u.active;
        if (userFilter === "approved") return u.approved && u.active && u.role !== "admin";
        if (userFilter === "admins") return u.role === "admin";
        if (userFilter === "inactive") return !u.active;
        if (userFilter === "contact") return !!u.contactPending;
        return true;
      })
      .filter((u) => {
        if (!term) return true;
        return (
          String(u.username || "").toLowerCase().includes(term) ||
          String(u.id || "").toLowerCase().includes(term)
        );
      });
  }, [users, userFilter, q]);

  const pendingCount = useMemo(() => (users || []).filter(u => !u.approved && u.active).length, [users]);

  if (!authed) return <div className="card">Please login.</div>;
  if (role !== "admin") return <div className="card">Admin only.</div>;

  return (
    <div className="grid" style={{ gap: 14 }}>
      {/* Header / actions */}
      <div className="card">
        <div className="cardHeader">
          <h2>Admin dashboard</h2>
          <div className="smallNote">{pendingCount ? `${pendingCount} awaiting approval` : "All clear"}</div>
        </div>

        <div className="adminToolbar">
          <input
            placeholder="Search username or id…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="pending">Awaiting approval</option>
            <option value="approved">Approved users</option>
            <option value="admins">Admins</option>
            <option value="inactive">Inactive</option>
            <option value="contact">Contact requests</option>
          </select>
          <button type="button" onClick={refresh}>Refresh</button>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
          <button
            type="button"
            className={pushEnabled ? "" : "btnPrimary"}
            onClick={async () => {
              try {
                setMsg("");
                if (pushEnabled) {
                  await disableAdminPush();
                  setPushEnabled(false);
                  showToast("Push notifications disabled", "ok");
                } else {
                  await initAdminPush();
                  setPushEnabled(true);
                  showToast("Push notifications enabled", "ok");
                }
              } catch (e) {
                setMsg(e.message);
              }
            }}
          >
            {pushEnabled ? "Disable admin push notifications" : "Enable admin push notifications"}
          </button>
          {msg ? <div className="msg" style={{ marginTop: 0 }}>{msg}</div> : null}
        </div>
        <div className="sep" />

        <div className="fieldRow">
          <div>
            <label>My default calculator</label>
            <select
              value={myDefaultRoute}
              onChange={async (e) => {
                const v = e.target.value;
                setMyDefaultRoute(v);
                try {
                  await api.adminPatchUser(myId, { defaultCalculatorRoute: v });
                  showToast("Default calculator saved", "ok");
                } catch (err) {
                  showToast(err.message || "Failed to save", "err");
                }
              }}
            >
              <option value="/">Calculator</option>
              <option value="/admin-calculator">Techemet Calculator</option>
              <option value="/percent-calculator">Price Calculator (%)</option>
              <option value="/basf-calculator">BASF Calculator</option>
              <option value="/contract-compare">Contract Compare</option>
            </select>
            <div className="smallNote">Used after login and on refresh.</div>
          </div>
        </div>

      </div>

      {/* Users table */}
      <div className="card">
        <div className="cardHeader">
          <h2>Users</h2>
          <div className="smallNote">{filteredUsers.length} shown</div>
        </div>

            <>
              {/* Desktop table */}
              <div className="desktopOnly">
                <div className="tableWrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th style={{ width: 80 }}>New</th>
                        <th>Status</th>
                        <th>Contact</th>
                        <th>Role</th>
                        <th>Margin</th>
                        <th>Joined</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((u) => (
                        <tr key={u.id}>
                          {/* Username */}
                          <td style={{ fontWeight: 800 }}>{u.username}</td>

                          {/* New (unseen logs) */}
                          <td>
                            {u.unseenCount > 0 ? <span className="badge">{u.unseenCount}</span> : <span className="smallNote">—</span>}
                          </td>

                          {/* Status */}
                          <td>
                            {u.active ? (
                              u.approved ? (
                                <span className="pill pillGood">
                                  <span className="pillDot" style={{ background: "var(--good)" }} />
                                  approved
                                </span>
                              ) : (
                                <span className="pill pillBad">
                                  <span className="pillDot" style={{ background: "var(--bad)" }} />
                                  pending
                                </span>
                              )
                            ) : (
                              <span className="pill pillBad">
                                <span className="pillDot" style={{ background: "var(--bad)" }} />
                                inactive
                              </span>
                            )}
                          </td>

                          {/* Contact */}
                          <td>
                            {u.contactPending ? (
                              <span className="pill pillBad">
                                <span className="pillDot" style={{ background: "var(--bad)" }} />
                                pending
                              </span>
                            ) : (
                              <span className="smallNote">—</span>
                            )}
                          </td>

                          {/* Role */}
                          <td>{u.role}</td>

                          {/* Margin */}
                          <td>
                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                              <div>{u.marginPct == null ? <span className="smallNote">global %</span> : `${fmtNum(u.marginPct, 2)}%`}</div>
                              <div>{u.marginUsdPerKg == null ? <span className="smallNote">global $/kg</span> : `$${fmtNum(u.marginUsdPerKg, 2)}/kg`}</div>
                            </div>
                          </td>

                          {/* Joined */}
                          <td>{fmtDate(u.createdAt)}</td>

                          {/* Action */}
                          <td style={{ textAlign: "right" }}>
                          <button
                            className={`tableBtn ${selected?.id === u.id ? "" : "btnPrimary"}`}
                            type="button"
                            onClick={() => {
                              if (selected?.id === u.id) closeUser();
                              else openUser(u.id);
                            }}
                          >
                            {selected?.id === u.id ? "Close" : "Open"}
                          </button>
                          </td>
                        </tr>
                      ))}
                      {!filteredUsers.length ? (
                        <tr>
                          <td colSpan={8} className="smallNote">No users match this filter.</td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile stacked rows */}
              <div className="mobileOnly">
                {filteredUsers.length ? (
                  filteredUsers.map((u) => (
                    <div className="mRow" key={u.id}>
                      <div className="mRowTop">
                        <div className="mRowTitle">{u.username}</div>
                        <div className="mRowTitle" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {u.unseenCount > 0 ? <span className="badge">{u.unseenCount}</span> : null}
                        </div>

                      </div>

                        <div className="mGrid">
                         <div className="mField">
                          <div className="mLabel">Joined</div>
                         <div className="mRowMeta">{fmtDate(u.createdAt)}</div>
                               </div>
                          <div className="mField">
                            <div className="mLabel">New</div>
                            <div className="mValue">{u.unseenCount > 0 ? u.unseenCount : "—"}</div>
                          </div>

                          <div className="mField">
                            <div className="mLabel">Status</div>
                            <div
                              className="mValue mStrong"
                              style={{ color: u.active ? (u.approved ? "var(--good)" : "var(--bad)") : "var(--bad)" }}
                            >
                              {u.active ? (u.approved ? "approved" : "pending") : "inactive"}
                            </div>
                          </div>

                          <div className="mField">
                            <div className="mLabel">Contact</div>
                            <div className="mValue">{u.contactPending ? "pending" : "—"}</div>
                          </div>

                          <div className="mField">
                            <div className="mLabel">Role</div>
                            <div className="mValue">{u.role}</div>
                          </div>

                          <div className="mField">
                            <div className="mLabel">Margin</div>
                            <div className="mValue">{u.marginPct == null ? "global" : `${fmtNum(u.marginPct, 2)}%`}</div>
                          </div>

                            <button
                              type="button"
                              className={selected?.id === u.id ? "" : "btnPrimary"}
                              onClick={() => {
                                if (selected?.id === u.id) closeUser();
                                else openUser(u.id);
                              }}
                            >
                              {selected?.id === u.id ? "Close" : "Open"}
                            </button>
                        </div>
                    </div>
                  ))
                ) : (
                  <div className="smallNote">No users match this filter.</div>
                )}
              </div>
            </>

        <div className="sep" />

        <div className="fieldRow">
          <input
            placeholder="Open by userId (paste)…"
            value={openUserId}
            onChange={(e) => setOpenUserId(e.target.value)}
          />
          <button type="button" onClick={() => openUser(openUserId)}>Open</button>
        </div>
      </div>

      {/* Selected user details */}
      <div className="gridCalc">
        <div className="card">
          <div className="cardHeader">
            <h2>User details</h2>
            <div className="row" style={{ gap: 8 }}>
              <div className="smallNote">{selected ? selected.username : "—"}</div>
              {selected ? (
                <button type="button" onClick={closeUser}>
                  Close
                </button>
              ) : null}
            </div>
          </div>

          {selected ? (
            <>
              <div className="chips" style={{ marginBottom: 10 }}>
                <span className={`chip ${selected.approved ? "chipGood" : "chipBad"}`}>
                  {selected.approved ? "approved" : "awaiting approval"}
                </span>
                <span className={`chip ${selected.active ? "chipGood" : "chipBad"}`}>
                  {selected.active ? "active" : "deactivated"}
                </span>
                <span className="chip">{selected.role}</span>
                {selected.marginPct != null ? (
                  <span className="chip">margin {fmtNum(selected.marginPct, 2)}%</span>
                ) : (
                  <span className="chip">margin: global</span>
                )}
                <span className="chip">joined {fmtDate(selected.createdAt)}</span>
              </div>

              <div className="twoCol">
                <button type="button" onClick={() => patchUser(selected.id, { approved: !selected.approved })}>
                  {selected.approved ? "Revoke approval" : "Approve user"}
                </button>
                  {selected.contactRequest?.pending ? (
                    <>
                      <div className="sep" />
                      <div className="card" style={{ padding: 12 }}>
                        <div className="cardHeader">
                          <h3 style={{ margin: 0 }}>Pending contact request</h3>
                          <div className="smallNote">{selected.contactRequest?.requestedAt ? fmtDate(selected.contactRequest.requestedAt) : ""}</div>
                        </div>

                        <div className="smallNote" style={{ marginBottom: 8 }}>
                          WhatsApp: <b>{selected.contactRequest?.whatsapp}</b>
                        </div>

                        <button
                          type="button"
                          className="btnPrimary"
                          onClick={async () => {
                            try {
                              await api.adminContactHandled(selected.id);
                              showToast("Marked handled", "ok");
                              await openUser(selected.id);
                              await refresh();
                            } catch (e) {
                              setMsg(e.message);
                            }
                          }}
                        >
                          Mark handled
                        </button>
                      </div>
                    </>
                  ) : null}
                <button type="button" onClick={() => patchUser(selected.id, { active: !selected.active })}>
                  {selected.active ? "Deactivate user" : "Activate user"}
                </button>

                <button
                  type="button"
                  onClick={() => patchUser(selected.id, { role: selected.role === "admin" ? "user" : "admin" })}
                >
                  Make {selected.role === "admin" ? "User" : "Admin"}
                </button>

                <button type="button" onClick={() => openPasswordModal(selected.id, selected.username)}>
                  Change password
                </button>

                <button
                  type="button"
                  className="btnDanger"
                  onClick={() => requestDeleteUser(selected.id, selected.username)}
                >
                  Delete user
                </button>
              </div>

              <div className="sep" />

              <label>User margin override (%)</label>
              <div className="row" style={{ gridTemplateColumns: "1fr auto", gap: 10 }}>
                <input
                  type="number"
                  placeholder="Leave empty to use global"
                  value={selected.marginPct ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelected({ ...selected, marginPct: v === "" ? null : Number(v) });
                  }}
                />
                <button
                  type="button"
                  className="btnPrimary"
                  onClick={() => patchUser(selected.id, { marginPct: selected.marginPct })}
                >
                  Save
                </button>
              </div>
              <div className="smallNote">If empty, global margin is used.</div>
              
              <div className="sep" />

              <label>User margin override (USD per kg)</label>
              <div className="row" style={{ gridTemplateColumns: "1fr auto", gap: 10 }}>
                <input
                  type="number"
                  placeholder="Leave empty to use global"
                  value={selected?.marginUsdPerKg ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelected({ ...selected, marginUsdPerKg: v === "" ? null : Number(v) });
                  }}
                />
                <button
                  type="button"
                  className="btnPrimary"
                  onClick={() => patchUser(selected.id, { marginUsdPerKg: selected?.marginUsdPerKg })}
                >
                  Save
                </button>
              </div>
              <div className="smallNote">If empty, global USD/kg margin is used.</div>

            </>
          ) : (
            <div className="smallNote">Select a user to view details.</div>
          )}
        </div>

          {/* User logs */}
          <div className="card">
            <div className="cardHeader">
              <h2>User activity</h2>
              <div className="smallNote">{selected ? `${selectedLogs.length} logs` : "—"}</div>
            </div>

            {!selected ? (
              <div className="smallNote">Open a user to see their calculator history.</div>
            ) : !selectedLogs.length ? (
              <div className="smallNote">No activity yet.</div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="desktopOnly">
                  <div className="tableWrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Weight</th>
                          <th>PPM</th>
                          <th>LME</th>
                          <th>LME unit</th>
                          <th>Net USD</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedLogs.slice(0, 200).map((x) => (
                          <tr key={x._id}>
                            <td>{fmtDate(x.createdAt)}</td>
                            <td>
                              {fmtNum(x.input?.weight, 0)} {x.input?.weightUnit}
                            </td>

                            <td className="wrapCell">
                              Pt {x.input?.ptPpm != null ? fmtNum(x.input.ptPpm, 0) : "—"} •
                              Pd {x.input?.pdPpm != null ? fmtNum(x.input.pdPpm, 0) : "—"} •
                              Rh {x.input?.rhPpm != null ? fmtNum(x.input.rhPpm, 0) : "—"}
                            </td>

                            <td className="wrapCell">
                              Pt {x.input?.lme?.pt != null ? fmtNum(x.input.lme.pt, 2) : "—"} •
                              Pd {x.input?.lme?.pd != null ? fmtNum(x.input.lme.pd, 2) : "—"} •
                              Rh {x.input?.lme?.rh != null ? fmtNum(x.input.lme.rh, 2) : "—"}
                            </td>

                            <td>{x.input?.lmeUnit === "per_g" ? "per gram" : "per ounce"}</td>
                            <td style={{ fontWeight: 800 }}>{fmtUsd(x.result?.netUsd)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile stacked rows */}
                <div className="mobileOnly">
                  {selectedLogs.slice(0, 200).map((x) => (
                    <div className="mRow" key={x._id}>
                      <div className="mRowTop">
                        <div className="mRowTitle">{fmtUsd(x.result?.netUsd)}</div>
                        <div className="mRowMeta">{fmtDate(x.createdAt)}</div>
                      </div>

                      <div className="mGrid">
                        <div className="mField">
                          <div className="mLabel">Weight</div>
                          <div className="mValue">
                            {fmtNum(x.input?.weight, 0)} {x.input?.weightUnit}
                          </div>
                        </div>

                        <div className="mField">
                          <div className="mLabel">PPM</div>
                          <div className="mValue">
                            Pt {x.input?.ptPpm != null ? fmtNum(x.input.ptPpm, 0) : "—"} •
                            Pd {x.input?.pdPpm != null ? fmtNum(x.input.pdPpm, 0) : "—"} •
                            Rh {x.input?.rhPpm != null ? fmtNum(x.input.rhPpm, 0) : "—"}
                          </div>
                        </div>

                        <div className="mField">
                          <div className="mLabel">LME</div>
                          <div className="mValue">
                            Pt {x.input?.lme?.pt != null ? fmtNum(x.input.lme.pt, 2) : "—"} •
                            Pd {x.input?.lme?.pd != null ? fmtNum(x.input.lme.pd, 2) : "—"} •
                            Rh {x.input?.lme?.rh != null ? fmtNum(x.input.lme.rh, 2) : "—"}
                          </div>
                        </div>

                        <div className="mField">
                          <div className="mLabel">Unit</div>
                          <div className="mValue">{x.input?.lmeUnit === "per_g" ? "per gram" : "per ounce"}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
      </div>

      {/* Settings editor */}
      <div className="card">
        <div className="cardHeader">
          <h2>Calculator settings</h2>
          <div className="smallNote">Global controls</div>
        </div>

        {settings ? (
          <>
            <div className="fieldRow">
              <div>
                <label>Global margin %</label>
                <input
                  type="number"
                  value={settings.globalMarginPct ?? 0}
                  onChange={(e) => setSettings({ ...settings, globalMarginPct: Number(e.target.value) })}
                />
              </div>
              
              <div>
                <label>Global margin (USD per kg)</label>
                <input
                  type="number"
                  value={settings.globalMarginUsdPerKg ?? 0}
                  onChange={(e) => setSettings({ ...settings, globalMarginUsdPerKg: Number(e.target.value) })}
                />
              </div>

              <div>
                <label>Moisture % (deducted from weight)</label>
                <input
                  type="number"
                  value={settings.moisturePct ?? 0}
                  onChange={(e) => setSettings({ ...settings, moisturePct: Number(e.target.value) })}
                />
              </div>
            </div>

            <div className="sep" />

            <div className="sectionTitle">Charges (Lease Rates %)</div>
            <div className="row3">
              <div>
                <label>Pt</label>
                <input
                  type="number"
                  value={settings.chargeUsdPerG?.pt ?? 0}
                  onChange={(e) =>
                    setSettings({ ...settings, chargeUsdPerG: { ...settings.chargeUsdPerG, pt: Number(e.target.value) } })
                  }
                />
              </div>
              <div>
                <label>Pd</label>
                <input
                  type="number"
                  value={settings.chargeUsdPerG?.pd ?? 0}
                  onChange={(e) =>
                    setSettings({ ...settings, chargeUsdPerG: { ...settings.chargeUsdPerG, pd: Number(e.target.value) } })
                  }
                />
              </div>
              <div>
                <label>Rh</label>
                <input
                  type="number"
                  value={settings.chargeUsdPerG?.rh ?? 0}
                  onChange={(e) =>
                    setSettings({ ...settings, chargeUsdPerG: { ...settings.chargeUsdPerG, rh: Number(e.target.value) } })
                  }
                />
              </div>
            </div>

            <div className="sep" />

            <div className="sectionTitle">Lease days</div>
            <div className="row3">
              <div>
                <label>Pt</label>
                <input
                  type="number"
                  value={settings.leaseDays?.pt ?? 0}
                  onChange={(e) => setSettings({ ...settings, leaseDays: { ...settings.leaseDays, pt: Number(e.target.value) } })}
                />
              </div>
              <div>
                <label>Pd</label>
                <input
                  type="number"
                  value={settings.leaseDays?.pd ?? 0}
                  onChange={(e) => setSettings({ ...settings, leaseDays: { ...settings.leaseDays, pd: Number(e.target.value) } })}
                />
              </div>
              <div>
                <label>Rh</label>
                <input
                  type="number"
                  value={settings.leaseDays?.rh ?? 0}
                  onChange={(e) => setSettings({ ...settings, leaseDays: { ...settings.leaseDays, rh: Number(e.target.value) } })}
                />
              </div>
            </div>

            <div className="sep" />

            <label>Admin notification on calculation</label>
            <select
              value={settings.notifyOnCalc ? "yes" : "no"}
              onChange={(e) => setSettings({ ...settings, notifyOnCalc: e.target.value === "yes" })}
            >
              <option value="yes">enabled</option>
              <option value="no">disabled</option>
            </select>

            <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <button type="button" className="btnPrimary" onClick={saveSettings}>
                Save settings
              </button>
              <span className="smallNote">Settings apply to new calculations and are snapshotted into logs.</span>
            </div>
          </>
        ) : (
          <div className="smallNote">Loading settings…</div>
        )}
      </div>
      {toast ? (
      <div className={`toast ${toast.type === "err" ? "toastErr" : "toastOk"}`}>
        {toast.text}
      </div>
    ) : null}

    {confirmDelete ? (
      <div className="modalBackdrop">
        <div className="modalCard">
          <h3>Delete user</h3>

          <p>
            Are you sure you want to permanently delete{" "}
            <b>{confirmDelete.username}</b>?
          </p>

          <p className="smallNote">
            This action cannot be undone.
          </p>

          <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
            <button
              type="button"
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </button>

            <button
              type="button"
              className="btnDanger"
              onClick={confirmDeleteUser}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    ) : null}

    {pwModal ? (
      <div className="modalBackdrop">
        <div className="modalCard">
          <h3>Change password</h3>

          <p className="smallNote">
            User: <b>{pwModal.username}</b>
          </p>

          <label>New password</label>
          <input
            type="password"
            value={pwModal.password}
            onChange={(e) => setPwModal({ ...pwModal, password: e.target.value })}
            placeholder="Enter new password"
            autoFocus
          />

          <div className="row" style={{ justifyContent: "flex-end", gap: 10, marginTop: 12 }}>
            <button type="button" onClick={() => setPwModal(null)}>
              Cancel
            </button>
            <button type="button" className="btnPrimary" onClick={submitPasswordModal}>
              Save
            </button>
          </div>
        </div>
      </div>
    ) : null}

    </div>
  );
}